import { CommonModule } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EntityEditModalComponent } from '@app/pages/incidents/details-page/entity/entity-edit-modal/entity-edit-modal.component';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { EntityModel } from '@app/models/entity.model';
import { CountryDescriptor, CountryFlags } from '@app/enums/country';
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { NumberOfEmployeesDescriptor } from '@app/enums/number-of-employees';
import { EntitySizeDescriptor, ESAdditionalInformationDescriptor } from '@app/enums/entity-size';
import { IncidentDetailsPageStore } from '../incident-details-page.store';
import { FieldReferenceService } from '../field-references.service';

@Component({
  standalone: true,
  selector: 'app-entity',
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule],
  templateUrl: './entity.component.html',
  styleUrl: './entity.component.scss',
})
export class EntityComponent {
  public CountryDescriptor: Record<string, string> = CountryDescriptor;
  public CountryFlags: Record<string, string> = CountryFlags;
  public businessSectorHR: Record<string, string> = BusinessSectorDescriptor;
  public noOfEmployeesHR: Record<string, string> = NumberOfEmployeesDescriptor;
  public entitySizeHR: Record<string, string> = EntitySizeDescriptor;
  public entitySizeThreshold = Object.keys(ESAdditionalInformationDescriptor)
    .map((k) => ({ [k]: (ESAdditionalInformationDescriptor as Record<string, string>)[k] }))
    .reduce((acc, next) => ({ ...acc, ...next }), {});

  constructor(
    protected readonly incidentsPageStore: IncidentDetailsPageStore,
    private readonly fieldReferencesService: FieldReferenceService,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
  ) {}

  public async openEntityEdit(): Promise<void> {
    const dialogRef = this.dialog.open<EntityEditModalComponent, EntityModel | null, EntityModel | undefined>(
      EntityEditModalComponent,
      {
        width: '90vw',
        maxWidth: '1400px',
        disableClose: true,
        injector: this.injector,
        data: this.incidentsPageStore.incident()!.entity,
      },
    );

    const chosenEntity = await firstValueFrom(dialogRef.afterClosed());
    if (chosenEntity) {
      this.incidentsPageStore.incident()!.entity = chosenEntity;
      this.incidentsPageStore.updateIncidentRootField('entityId', chosenEntity.id);
      this.incidentsPageStore.updateIncidentRootField('entity', chosenEntity);

      // If the entity was coming from a field reference, we need to uncheck it in the field references collection. If the entity is manual, it won't do anything.
      this.fieldReferencesService.uncheckIncidentFieldReference('EntityId');
    }
  }

  public async onRemoveEntity(): Promise<void> {
    const dialogResult = await this.openConfirmationDialogAsync(
      'Remove Entity',
      'Warning:\nCurrent entity will be deleted.',
    );

    if (dialogResult) {
      this.incidentsPageStore.incident()!.entity = null;
      this.incidentsPageStore.updateIncidentRootField('entityId', null);
      this.incidentsPageStore.updateIncidentRootField('entity', null);

      // If the entity was coming from a field reference, we need to uncheck it in the field references collection. If the entity is manual, it won't do anything.
      this.fieldReferencesService.uncheckIncidentFieldReference('EntityId');
    }
  }

  private async openConfirmationDialogAsync(title: string, content: string): Promise<boolean | undefined> {
    const dialogRef = this.dialog.open<ConfirmationModalComponent, { title: string; content: string }, boolean>(
      ConfirmationModalComponent,
      {
        width: '20vw',
        disableClose: true,
        injector: this.injector,
        data: {
          title: title,
          content: content,
        },
      },
    );

    return await firstValueFrom(dialogRef.afterClosed());
  }
}
